import { useTranslation } from "react-i18next";
import { Search, X } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import {
  NOTIFICATION_CHANNELS,
  type CommunicationLocale,
  type NotificationChannel,
} from "../shared/communications-types";

interface CommunicationsFiltersProps {
  readonly keyQuery: string;
  readonly channel: NotificationChannel | null;
  readonly locale: string | null;
  readonly locales: readonly CommunicationLocale[];
  readonly onKeyQueryChange: (value: string) => void;
  readonly onChannelChange: (value: NotificationChannel | null) => void;
  readonly onLocaleChange: (value: string | null) => void;
  readonly onReset: () => void;
}

const SELECT_CLASS =
  "h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

export function CommunicationsFilters({
  keyQuery,
  channel,
  locale,
  locales,
  onKeyQueryChange,
  onChannelChange,
  onLocaleChange,
  onReset,
}: CommunicationsFiltersProps) {
  const { t } = useTranslation();
  const hasFilters = keyQuery.length > 0 || channel !== null || locale !== null;

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="min-w-[260px] flex-1 space-y-1.5">
        <label
          htmlFor="communications-key-search"
          className="font-sans text-sm font-medium text-foreground"
        >
          {t("communications.filters.search")}
        </label>
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            id="communications-key-search"
            data-testid="communications-key-search-input"
            type="search"
            value={keyQuery}
            onChange={(e) => onKeyQueryChange(e.target.value)}
            placeholder={t("communications.filters.searchPlaceholder")}
            className="pl-9"
            autoComplete="off"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <label
          htmlFor="communications-channel-filter"
          className="font-sans text-sm font-medium text-foreground"
        >
          {t("communications.filters.channel")}
        </label>
        <select
          id="communications-channel-filter"
          data-testid="filter-channel"
          value={channel ?? ""}
          onChange={(e) => {
            const value = e.target.value;
            onChannelChange(value === "" ? null : (value as NotificationChannel));
          }}
          className={SELECT_CLASS}
        >
          <option value="">{t("communications.filters.channelAll")}</option>
          {NOTIFICATION_CHANNELS.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-1.5">
        <label
          htmlFor="communications-locale-filter"
          className="font-sans text-sm font-medium text-foreground"
        >
          {t("communications.filters.locale")}
        </label>
        <select
          id="communications-locale-filter"
          data-testid="filter-locale"
          value={locale ?? ""}
          onChange={(e) => {
            const value = e.target.value;
            onLocaleChange(value === "" ? null : value);
          }}
          className={SELECT_CLASS}
        >
          <option value="">{t("communications.filters.localeAll")}</option>
          {locales.map((l) => (
            <option key={l.code} value={l.code}>
              {l.code}
            </option>
          ))}
        </select>
      </div>

      {hasFilters && (
        <Button
          data-testid="communications-filters-reset"
          variant="ghost"
          onClick={onReset}
          className="h-10"
        >
          <X className="mr-1 h-4 w-4" aria-hidden="true" />
          {t("communications.filters.reset")}
        </Button>
      )}
    </div>
  );
}
